/* START_MODULE_CONTRACT
 * PURPOSE: Хук готовности формы внутри MUI Dialog: игнорировать ложный blur до onEntered.
 * SCOPE: formReady, resetFormReady, dialogTransitionSlotProps.
 * DEPENDS: react
 * LINKS: M-FE-APP
 * ROLE: RUNTIME
 * MAP_MODE: EXPORTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * useDialogFormReady - состояние готовности формы после анимации открытия диалога.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-13 - Отложенный focus первого поля после Fade; firstFieldRef вместо autoFocus.
 * END_CHANGE_SUMMARY
 */

import { useCallback, useMemo, useState } from 'react';

/* START_METHOD_useDialogFormReady */
/* START_CONTRACT: useDialogFormReady
 * PURPOSE: Отслеживать момент завершения анимации открытия диалога для безопасной blur-валидации.
 * INPUTS: { onEntered?: () => void, firstFieldRef?: { current: HTMLElement|null } }.
 * OUTPUTS: { formReady: boolean, resetFormReady: () => void, dialogTransitionSlotProps: object }.
 * SIDE_EFFECTS: локальный state.
 * LINKS: M-FE-APP
 * END_CONTRACT: useDialogFormReady
 */
export function useDialogFormReady({ onEntered, firstFieldRef } = {}) {
  const [formReady, setFormReady] = useState(false);

  const resetFormReady = useCallback(() => {
    setFormReady(false);
  }, []);

  const dialogTransitionSlotProps = useMemo(() => ({
    onEntered: (...args) => {
      setFormReady(true);
      onEntered?.(...args);
      // Фокус после анимации и кадра отрисовки — иначе каретка «пропадает» (Win11/Chromium).
      if (firstFieldRef?.current) {
        requestAnimationFrame(() => {
          firstFieldRef.current?.focus({ preventScroll: true });
        });
      }
    },
  }), [onEntered, firstFieldRef]);

  return {
    formReady,
    resetFormReady,
    dialogTransitionSlotProps,
  };
}
/* END_METHOD_useDialogFormReady */
